import type { PositionWithAsset } from "types/Portfolio";
import { PANEL_TYPES, type PanelTypeWithCrypto } from "features/market/api/MarketQuotes";
import { isOpenPortfolioPosition } from "shared/portfolio/positions";
export { isOpenPortfolioPosition, OPEN_POSITION_QUANTITY_EPSILON } from "shared/portfolio/positions";

export type QuoteCurrency = "ARS" | "USD";

/**
 * Argentine market convention for bi-monetary tickers:
 * - "DD" suffix = USD (e.g. YPFDD)
 * - "D" suffix  = USD (e.g. BBARD, AAPLD), except when DD variant exists (YPFD = ARS)
 * - ".D" suffix = USD (e.g. BMA.D)
 * - "C" suffix  = ARS (e.g. ARKKC)
 * - no suffix   = ARS (e.g. BBAR, AAPL)
 *
 * Note: the DD edge case (YPFD = ARS when YPFDD exists) is handled in
 * parseRowsToQuotes where group context is available. This function treats
 * single-D as USD which is correct for the majority of tickers.
 */
export function getCurrencyForSymbol(symbol: string): QuoteCurrency {
  const s = String(symbol ?? "").trim().toUpperCase();
  if (s.endsWith("DD")) return "USD";
  if (s.endsWith(".D")) return "USD";
  if (s.endsWith("D")) return "USD";
  if (s.endsWith("C") || s.endsWith(".C")) return "ARS";
  return "ARS";
}

/**
 * Strips known bi-monetary suffixes without corrupting symbols that naturally end in C/D.
 */
function stripKnownTickerSuffix(symbol: string): string {
  const s = String(symbol ?? "").trim().toUpperCase();
  if (s.endsWith("DD")) return s.slice(0, -2);
  if (s.endsWith(".D")) return s.slice(0, -2);
  if (s.endsWith("D")) return s.slice(0, -1);
  if (s.endsWith(".C")) return s.slice(0, -2);
  if (s.endsWith("C")) return s.slice(0, -1);
  return s;
}

function uniqueInOrder(candidates: string[]): string[] {
  return [...new Set(candidates.filter(Boolean))];
}

/**
 * Returns the ticker(s) to try when looking up a price for a position.
 * Candidates are ordered by currency preference so the first quote match uses
 * the correct denomination (ARS vs USD).
 *
 * ARS view: ARS/base variants first, USD variants as fallback.
 * USD view: USD variants first, ARS/base variants as fallback.
 */
export function getTickerForPriceLookup(
  symbol: string,
  currencyView: QuoteCurrency
): string[] {
  const s = String(symbol ?? "").trim().toUpperCase();
  if (!s) return [];

  const base = stripKnownTickerSuffix(s);

  if (currencyView === "ARS") {
    const primary: string[] = [];
    if (getCurrencyForSymbol(s) === "ARS") primary.push(s);
    if (base !== s && getCurrencyForSymbol(base) === "ARS") primary.push(base);
    if (base === s) primary.push(base);
    if (!s.endsWith("C") && !s.endsWith(".C")) primary.push(`${base}C`);

    const fallback: string[] = [];
    if (getCurrencyForSymbol(s) === "USD") fallback.push(s);
    if (!s.endsWith("D") && !s.endsWith("DD") && !s.endsWith(".D")) {
      fallback.push(`${base}D`, `${base}DD`);
    }

    return uniqueInOrder([...primary, ...fallback]);
  }

  const primary: string[] = [];
  if (getCurrencyForSymbol(s) === "USD") primary.push(s);
  if (!s.endsWith("D") && !s.endsWith("DD") && !s.endsWith(".D")) {
    primary.push(`${base}D`, `${base}DD`);
  }

  const fallback: string[] = [];
  if (getCurrencyForSymbol(s) === "ARS") fallback.push(s);
  if (base !== s) fallback.push(base);
  if (!s.endsWith("C") && !s.endsWith(".C")) fallback.push(`${base}C`);

  return uniqueInOrder([...primary, ...fallback]);
}

export function getPortfolioQuotePanelsToLoad(
  positions: PositionWithAsset[]
): PanelTypeWithCrypto[] {
  const panels = new Set<PanelTypeWithCrypto>();
  let shouldDiscoverAcrossIolPanels = false;

  for (const position of positions) {
    if (!isOpenPortfolioPosition(position)) continue;
    const asset = position.assetId;
    if (!asset) continue;

    if (asset.marketPanel === "crypto") {
      panels.add("crypto");
      continue;
    }

    if (asset.type === "market" && asset.marketPanel) {
      panels.add(asset.marketPanel);
    }

    if (asset.symbol?.trim()) {
      shouldDiscoverAcrossIolPanels = true;
    }
  }

  if (shouldDiscoverAcrossIolPanels) {
    PANEL_TYPES.forEach((panel) => panels.add(panel));
  }

  return [...panels];
}

function normalizeAssetName(name: string): string {
  const raw = String(name ?? "").trim().toLowerCase();
  return raw.replace(/^cedear\s+/, "").trim() || raw;
}

/**
 * Group positions by normalized paper name (e.g. BBAR + BBARD both "Bbva"),
 * then pick one position per paper matching the currency view.
 * Custom assets and non-groupable positions are kept as separate rows.
 */
export function groupPositionsByPaper(
  positions: PositionWithAsset[],
  currencyView: QuoteCurrency
): PositionWithAsset[] {
  const marketPositions: PositionWithAsset[] = [];
  const customOrOther: PositionWithAsset[] = [];

  for (const p of positions) {
    if (!isOpenPortfolioPosition(p)) continue;
    const asset = p.assetId;
    if (asset?.type === "market" && asset.marketPanel && (asset.name || asset.symbol)) {
      marketPositions.push(p);
    } else {
      customOrOther.push(p);
    }
  }

  const byName = new Map<string, PositionWithAsset[]>();
  for (const p of marketPositions) {
    const name = p.assetId?.name ?? "";
    const symbol = p.assetId?.symbol ?? "";
    const key = normalizeAssetName(name) || symbol.toLowerCase();
    if (!key) continue;
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key)!.push(p);
  }

  const merged: PositionWithAsset[] = [];
  byName.forEach((group) => {
    const withCurrency = group.map((p) => ({
      pos: p,
      currency: p.assetId?.marketPanel === "crypto" ? "USD" as QuoteCurrency : getCurrencyForSymbol(p.assetId?.symbol ?? ""),
    }));
    const match = withCurrency.find((x) => x.currency === currencyView);
    const picked = match ?? withCurrency[0];
    merged.push(picked.pos);
  });

  return [...merged, ...customOrOther];
}
